import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Search, SlidersHorizontal } from "lucide-react";
import { CubeIcon } from "@heroicons/react/24/outline";
import { Helmet } from "react-helmet-async";
import ProductCard from "../ProductCard/ProductCard";
import { useProductDirectoryPage } from "../../hooks/useProductDirectoryPage";

export default function ProductDirectoryPage() {
  const {
    products,
    loading,
    searchInput,
    setSearchInput,
    selectedCategory,
    setSelectedCategory,
    selectedType,
    setSelectedType,
    totalCount,
    categoryFacets,
    typeFacets,
    hasMore,
    fetchMore,
  } = useProductDirectoryPage();

  return (
    <div className="relative min-h-full">
      <Helmet>
        <title>Product Directory</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <CubeIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Product Directory
            </h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 ml-9">
            Browse and discover products available in our storefront
          </p>
        </div>

        <div className="relative mb-4">
          <input
            className="w-full rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 pl-10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search products..."
            type="text"
            value={searchInput}
          />
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={20}
          />
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <SlidersHorizontal className="h-4 w-4" />
            <span>Filters</span>
          </div>

          <select
            className="rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm text-gray-900 dark:text-white"
            onChange={(e) => setSelectedCategory(e.target.value)}
            value={selectedCategory}
          >
            <option value="">All Categories</option>
            {Object.entries(categoryFacets)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([name, count]) => (
                <option key={name} value={name}>
                  {name} ({count})
                </option>
              ))}
          </select>

          <select
            className="rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm text-gray-900 dark:text-white"
            onChange={(e) => setSelectedType(e.target.value)}
            value={selectedType}
          >
            <option value="">All Types</option>
            {Object.entries(typeFacets)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([name, count]) => (
                <option key={name} value={name}>
                  {name} ({count})
                </option>
              ))}
          </select>
        </div>

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {totalCount} product{totalCount !== 1 ? "s" : ""} found
        </p>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 animate-pulse"
                key={i}
              >
                <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-t-lg" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 dark:bg-slate-600 rounded w-full" />
                  <div className="h-3 bg-gray-200 dark:bg-slate-600 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <InfiniteScroll
            dataLength={products.length}
            endMessage={
              products.length > 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500 dark:text-gray-400">
                    No more products to load
                  </p>
                </div>
              ) : null
            }
            hasMore={hasMore}
            loader={
              <div className="text-center py-4">
                <p className="text-gray-500 dark:text-gray-400">
                  Loading more...
                </p>
              </div>
            }
            next={fetchMore}
            scrollableTarget="main-scroll-area"
          >
            {products.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400">
                  No products found
                </p>
              </div>
            )}
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
}
